import { useTranslation } from "react-i18next";import React from 'react';
import { Settings as SettingsIcon, Bell, Lock, Globe, Palette } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Button } from '@/shared/components/ui/button';
import { Switch } from '@/shared/components/ui/switch';
import { Label } from '@/shared/components/ui/label';
import { Input } from '@/shared/components/ui/input';

export const Settings = () => {const { t } = useTranslation("ezparcel");
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("settings", "Settings")}</h1>
        <p className="text-muted-foreground mt-2">{t("configureYourAccount", "Configure your account preferences and system settings")}

        </p>
      </div>

      <Tabs defaultValue="general" className="space-y-4">
        <TabsList>
          <TabsTrigger value="general">{t("general", "General")}</TabsTrigger>
          <TabsTrigger value="notifications">{t("notifications", "Notifications")}</TabsTrigger>
          <TabsTrigger value="security">{t("security", "Security")}</TabsTrigger>
          <TabsTrigger value="integrations">{t("integrations", "Integrations")}</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("companyInformation", "Company Information")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">{t("companyName", "Company Name")}</Label>
                <Input id="companyName" defaultValue="EzParcel Logistics" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t("email", "Email")}</Label>
                <Input id="email" type="email" defaultValue="admin@ezparcel.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t("phone", "Phone")}</Label>
                <Input id="phone" defaultValue="+1 (555) 123-4567" />
              </div>
              <Button className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent-hover))]">{t("saveChanges", "Save Changes")}

              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t("preferences", "Preferences")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Globe className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{t("language", "Language")}</p>
                    <p className="text-sm text-muted-foreground">{t("selectYourPreferred", "Select your preferred language")}</p>
                  </div>
                </div>
                <Button variant="outline">{t("englishUs", "English (US)")}</Button>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Palette className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{t("theme", "Theme")}</p>
                    <p className="text-sm text-muted-foreground">{t("chooseLightOr", "Choose light or dark theme")}</p>
                  </div>
                </div>
                <Button variant="outline">{t("systemDefault", "System Default")}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("notificationPreferences", "Notification Preferences")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t("emailNotifications", "Email Notifications")}</p>
                  <p className="text-sm text-muted-foreground">{t("receiveEmailAlerts", "Receive email alerts for important updates")}</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t("pushNotifications", "Push Notifications")}</p>
                  <p className="text-sm text-muted-foreground">{t("getPushNotifications", "Get push notifications on your device")}</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t("smsAlerts", "SMS Alerts")}</p>
                  <p className="text-sm text-muted-foreground">{t("receiveTextMessages", "Receive text messages for critical alerts")}</p>
                </div>
                <Switch />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t("deliveryUpdates", "Delivery Updates")}</p>
                  <p className="text-sm text-muted-foreground">{t("notificationsForDelivery", "Notifications for delivery status changes")}</p>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{t("exceptionAlerts", "Exception Alerts")}</p>
                  <p className="text-sm text-muted-foreground">{t("immediateAlertsFor", "Immediate alerts for failed deliveries")}</p>
                </div>
                <Switch defaultChecked />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("securitySettings", "Security Settings")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Lock className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium">{t("twofactorAuthentication", "Two-Factor Authentication")}</p>
                    <p className="text-sm text-muted-foreground">{t("addAnExtra", "Add an extra layer of security")}</p>
                  </div>
                </div>
                <Switch defaultChecked />
              </div>
              <div className="pt-4">
                <Button variant="outline">{t("changePassword", "Change Password")}</Button>
              </div>
              <div>
                <Button variant="outline">{t("viewActiveSessions", "View Active Sessions")}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="integrations" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("thirdpartyIntegrations", "Third-Party Integrations")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">{t("googleMapsApi", "Google Maps API")}</p>
                  <p className="text-sm text-muted-foreground">{t("connectedLastSync", "Connected \u2022 Last sync: 2 hours ago")}</p>
                </div>
                <Button variant="outline">{t("configure", "Configure")}</Button>
              </div>
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">{t("stripePaymentGateway", "Stripe Payment Gateway")}</p>
                  <p className="text-sm text-muted-foreground">{t("connectedActive", "Connected \u2022 Active")}</p>
                </div>
                <Button variant="outline">{t("configure", "Configure")}</Button>
              </div>
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">{t("twilioSms", "Twilio SMS")}</p>
                  <p className="text-sm text-muted-foreground">{t("connectedActive", "Connected \u2022 Active")}</p>
                </div>
                <Button variant="outline">{t("configure", "Configure")}</Button>
              </div>
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div>
                  <p className="font-medium">{t("openaiAiFeatures", "OpenAI (AI Features)")}</p>
                  <p className="text-sm text-muted-foreground">{t("connectedEmergentLlm", "Connected \u2022 Emergent LLM Key")}</p>
                </div>
                <Button variant="outline">{t("configure", "Configure")}</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};